import React from "react";
import { Plus, Tag, Image as ImageIcon, Star, X, PlusCircle } from "lucide-react";
import galleryIcon from "../../assets/img/gallery.png";

export default function ProductList({
  products,
  onAddToCart,
  loading = false,
  emptyTitle = "Tidak ada produk ditemukan",
  emptyMessage = "Coba pilih kategori lain atau ubah kata kunci pencarian",
  isBestSellerView = false,
  isManualBestSeller = false,
  onRemoveBestSeller,
  showStock = true,
  showImage = true,
}) {
  if (loading) {
    return (
      <div className="space-y-3 pb-4">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-xl border border-slate-100 p-3 flex items-center gap-3 animate-pulse"
          >
            <div className="w-14 h-14 bg-slate-100 rounded-lg shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-slate-100 rounded w-1/3" />
              <div className="h-3 bg-slate-100 rounded w-1/4" />
            </div>
            <div className="w-16 h-6 bg-slate-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const activeProducts = products || [];

  if (activeProducts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 bg-white rounded-2xl border border-dashed border-slate-200">
        <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-4">
          <Tag className="w-8 h-8 text-teal-600" />
        </div>
        <h3 className="text-base font-bold text-slate-800 mb-1">{emptyTitle}</h3>
        <p className="text-sm text-slate-500 text-center max-w-sm">{emptyMessage}</p>
      </div>
    );
  }

  // Group by product_id (NOT name) so that products with same name but
  // different units / IDs are treated as separate items.
  const grouped = activeProducts.reduce((acc, product) => {
    const key = product.product_id || product.id;
    if (!acc[key])
      acc[key] = {
        name: product.name,
        category: product.category,
        has_addons: Boolean(product.has_addons || (product.addon_groups && product.addon_groups.length > 0)),
        addon_groups: product.addon_groups || [],
        variants: [],
      };
    if (product.has_addons || (product.addon_groups && product.addon_groups.length > 0)) {
      acc[key].has_addons = true;
      if (product.addon_groups && product.addon_groups.length > 0) {
        acc[key].addon_groups = product.addon_groups;
      }
    }
    acc[key].variants.push(product);
    return acc;
  }, {});

  // Filter out the base "DEFAULT" entry only when real variants exist.
  // Safety: never let variants become empty.
  Object.values(grouped).forEach(group => {
    if (group.variants.length > 1) {
      const withVariants = group.variants.filter(v => v.variant_id != null);
      if (withVariants.length > 0) {
        group.variants = withVariants;
      }
    }
  });

  const groupList = Object.values(grouped).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const handleGroupClick = (group) => {
    onAddToCart({
      ...group.variants[0],
      _group: group,
      variants: group.variants,
      has_addons: Boolean(group.has_addons || group.variants[0]?.has_addons),
      addon_groups: group.addon_groups || group.variants[0]?.addon_groups || [],
    });
  };

  return (
    <>
      <div className="space-y-2">
        {groupList.map((group) => {
          const hasVariants = group.variants.some(v => v.variant_id != null || (v.variant_name && v.variant_name !== "DEFAULT"));
          const sellingPrices = group.variants[0]?.selling_prices || group.variants[0]?.product?.selling_prices || [];
          const activeSellingPrices = sellingPrices.filter(sp => sp.status === "ACTIVE" || !sp.status);
          const hasSellingPrices = activeSellingPrices.length > 0;
          const totalPricesCount = activeSellingPrices.length + 1;

          const allPrices = group.variants.flatMap((v) => {
            const list = [];
            const p = Number(v.price !== undefined ? v.price : v.sell_price);
            if (isFinite(p) && p > 0) list.push(p);
            const sps = v.selling_prices || v.product?.selling_prices || [];
            sps.forEach(sp => {
              if ((sp.status === "ACTIVE" || !sp.status) && isFinite(Number(sp.price)) && Number(sp.price) > 0) {
                list.push(Number(sp.price));
              }
            });
            if (v.options && v.options.length > 0) {
              v.options.forEach((o) => {
                const op = Number(o.price);
                if (isFinite(op) && op > 0) list.push(op);
              });
            }
            return list;
          });
          const safePrices = allPrices.length > 0 ? allPrices : [0];
          const uniquePrices = new Set(safePrices);
          const hasMultiplePrices = uniquePrices.size > 1;
          const minPrice = Math.min(...safePrices);
          const maxPrice = Math.max(...safePrices);
          const totalStock = group.variants.reduce(
            (sum, v) => sum + (v.stock || 0),
            0,
          );
          const unit = group.variants[0]?.unit || "Pcs";
          const imageUrl = group.variants[0]?.image_url;

          const groupKey = group.variants[0]?.product_id || group.variants[0]?.id || group.name;

          return (
            <div
              key={groupKey}
              onClick={() => handleGroupClick(group)}
              className="bg-white rounded-xl p-3 flex items-center justify-between gap-3 hover:bg-teal-50/50 hover:shadow-sm cursor-pointer transition-all border border-slate-100 hover:border-teal-200 group">
              
              {/* Product Thumbnail (4:3 aspect ratio) */}
              {showImage !== false && (
                imageUrl ? (
                  <div className="w-14 aspect-[4/3] rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 overflow-hidden relative">
                    {isBestSellerView && (
                      <div className="absolute top-0 left-0 bg-amber-500 text-white p-0.5 rounded-br-md shadow-sm z-10" title="Best Seller">
                        <Star className="w-2.5 h-2.5 fill-current" />
                      </div>
                    )}
                    <img
                      src={imageUrl?.startsWith('http') ? imageUrl : import.meta.env.VITE_API_URL + "/storage/" + imageUrl}
                      alt={group.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = galleryIcon;
                        e.target.className = "w-7 h-7 object-contain opacity-60";
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-14 aspect-[4/3] relative shrink-0 flex items-center justify-center bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
                    {isBestSellerView && (
                      <div className="absolute top-0 left-0 bg-amber-500 text-white p-0.5 rounded-br-md shadow-sm z-10" title="Best Seller">
                        <Star className="w-2.5 h-2.5 fill-current" />
                      </div>
                    )}
                    <img
                      src={galleryIcon}
                      alt={group.name}
                      className="w-7 h-7 object-contain opacity-70"
                    />
                  </div>
                )
              )}

              {/* Product Title & Stock Info (Middle) */}
              <div className="min-w-0 flex-1 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-0.5">
                  {isBestSellerView && showImage === false && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-600 text-xs rounded-md border border-amber-200 shrink-0 font-medium">
                      <Star className="w-3 h-3 text-amber-500 fill-amber-400" />
                      <span className="text-[10px] font-bold">Best Seller</span>
                    </span>
                  )}
                  <h3 className="font-bold text-gray-800 text-sm group-hover:text-teal-700 truncate">
                    {group.name}
                  </h3>
                </div>
                {showStock && (
                  <p className="text-xs text-gray-400 font-medium">
                    Stok: {totalStock} {unit}
                  </p>
                )}
              </div>

              {/* Right Side: Variant / Price Label (Top-Right) & Price (Bottom-Right) */}
              <div className="flex flex-col items-end justify-between self-stretch shrink-0 py-0.5 gap-1">
                {hasVariants ? (
                  <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-xs">
                    {group.variants.length} Varian
                  </span>
                ) : group.has_addons ? (
                  <span className="bg-teal-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-xs flex items-center gap-1">
                    <PlusCircle className="w-3 h-3" /> Addon
                  </span>
                ) : hasSellingPrices ? (
                  <span className="bg-teal-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-xs">
                    {totalPricesCount} Harga
                  </span>
                ) : (
                  <div className="h-4" />
                )}
                <span className="text-gray-900 font-bold text-sm sm:text-base">
                  {hasMultiplePrices
                    ? `Rp ${minPrice.toLocaleString("id-ID")} - Rp ${maxPrice.toLocaleString("id-ID")}`
                    : `Rp ${minPrice.toLocaleString("id-ID")}`}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* VariantPickerModal removed — Sales.jsx triggerAddToCart now handles all popups */}
    </>
  );
}
